import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  ArrayInput, TextInput, SelectInput, required, FormDataConsumer, WithPermissions
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import { SimpleFormIterator } from './SimpleFormIterator';
import Typography from '@material-ui/core/Typography';
import { getNameText } from '../../../utils';
import { choices } from '../../../components/WebsiteTypes';
import { getDepthChoices } from '../../../components/DepthChoices';
import { getFrequencyChoices } from '../../../components/FrequencyChoices';
import { validateCanonicalDomain } from '../../../utils/validateCanonicalDomain';
import HiddenInput from '../../../components/HiddenInput';
import AccountFieldForAdmin from '../../../components/AccountFieldForAdmin';
import TooltipHelper from './TooltipHelper';

const defaultPopUpTemplate = process.env.REACT_APP_API_DEFAULT_POP_UP_TEMPLATE;
const defaultDeclarationTemplate = process.env.REACT_APP_API_DEFAULT_DECLARATION_TEMPLATE;
const defaultChoiceBoxTemplate = process.env.REACT_APP_API_DEFAULT_CHOICE_BOX_TEMPLATE;

const noScanOptions = [...getFrequencyChoices(), ...getDepthChoices()].map(({id}) => ({id, name: 'No Scan'})); // we need to display a No Scan label if the domain type is internal or test in some select inputs, there is no easy way to do this as it currently stands, so we just need to replace all option names with "No scan"

const domainDetailsStyles = theme => ({
  container: {
    marginLeft: '40px',
    padding: '20px',
    '& .button-add-domains': {
      padding: '0',
    }
  },
	domainTypes: {
		paddingLeft:'0',

		'& li':{
			listStyleType: 'none',
			marginBottom:'5px',

			'& strong:first-child':{
				color:'#0D4464',
			}
		},
	},
  sectionHeader: {
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  input: {
    width: '550px',
  },
  scanningSettings: {
    marginTop: '0',
  },
  disabled: {
    color: 'rgba(0, 0, 0, 0.38)',
  },
  iteratorElement: {
    '&& section > div': {
      display: 'inline-flex',
    },
  },
});


export class DomainDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      recordLiveValues: null
    }
  }

  componentWillUpdate(nextProps) {
    if (nextProps.recordLiveValues && this.props.recordLiveValues && (this.props.recordLiveValues.account !== nextProps.recordLiveValues.account)) {
      this.props.onAccountChange(nextProps.recordLiveValues.account);
    }
  }

  render() {
    const { classes, templates, ...rest } = this.props;

    return (
      <div>
        <WithPermissions render={({ permissions }) => (
          permissions === 'ROLE_ADMIN' ?
            <Typography variant="h5" gutterBottom className={classes.sectionHeader}>Account</Typography> : null
          )}
        />
        <div className={classes.container}>
          <AccountFieldForAdmin className={classes.input}/>
        </div>
        <Typography variant="h5" gutterBottom className={classes.sectionHeader}>Domain details</Typography>
        <div className={classes.container}>
          <ArrayInput fullWidth={false} label="" source="domains" className={classes.scanningSettings}
                      initialValue={[{type: 'website', scanningDepth: 5000, scanningFrequency: 7}]}>
            <SimpleFormIterator disableRemove disableAdd className={classes.iteratorElement}>
              <TextInput className={classes.input}
                         label="Enter the URL of your domain"
                         data-testid="input-domain-name"
                         source="canonicalDomain"
                         parse={domain => domain.trim().toLowerCase()}
                         validate={validateCanonicalDomain()} />
              <TooltipHelper title="Please format the domain as yourdomain.com, without http, https, or www."
                             placement="right-end" />
              <SelectInput className={classes.input}
                           id="domain-type"
                           label="Type"
                           source="type"
                           data-testid="select-domain-type"
                           choices={choices()}
                           validate={required()} />
              <TooltipHelper
                title={<>Public Domain: We scan your website and collect consent from all your visitors once you've added a pop-up on your your website. Scans start automatically and can take up to 24 hours to finish. <br/><br/>
Public Test Domain: We don't scan your website, but the URL is included in the pop-up and cookie policy when you add those to your website.<br/><br/>
                                                    Internal Test Domain: We don't scan the website or collect consent from visitors. </>}
                placement="right"
              />
              <FormDataConsumer>
                {({
                    formData,
                    scopedFormData,
                    getSource,
                    ...rest
                  }) =>{
                  const isWebsite = scopedFormData?.type === 'website';
                  return <div>
                      <SelectInput
                        className={classes.input}
                        id="domain-scanning-depth"
                        label="Scanning depth"
                        source={getSource('scanningDepth')}
                        choices={isWebsite ? getDepthChoices() : noScanOptions}
                        validate={required()}
                        disabled={!isWebsite}
                      />
                      <TooltipHelper title="The number of pages on your website you want us to scan." placement="right" />
                      <SelectInput
                        className={classes.input}
                        id="domain-scanning-frequency"
                        label="Scanning frequency"
                        source={getSource('scanningFrequency')}
                        choices={isWebsite ? getFrequencyChoices() : noScanOptions}
                        validate={required()}
                        disabled={!isWebsite}
                      />
                      <TooltipHelper title="How often we scan your website." placement="right" />
                  </div>

                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>


          {templates['pop_up_templates'] ?
            <HiddenInput label="Consent Pop-up"
                         source="popUpTemplate"
                         optionText={getNameText}
                         optionValue="@id"
                         initialValue={defaultPopUpTemplate}
                         choices={templates['pop_up_templates']}
                         validate={required()} /> : null
          }
          {templates['declaration_templates'] ?
            <HiddenInput label="Cookie Policy"
                         source="declarationTemplate"
                         optionText={getNameText}
                         initialValue={defaultDeclarationTemplate}
                         optionValue="@id"
                         choices={templates['declaration_templates']}
                         validate={required()} /> : null
          }
          {templates['choice_box_templates'] ?
            <HiddenInput label="Privacy Controls"
                         source="choiceBoxTemplate"
                         optionText={getNameText}
                         initialValue={defaultChoiceBoxTemplate}
                         optionValue="@id"
                         choices={templates['choice_box_templates']}
                         validate={required()} /> : null
          }
          <FormSpy subscription={{ values: true }}
            onChange={({ values }) => {this.setState({ recordLiveValues: values })}} >
          </FormSpy>
        </div>
      </div>
    );
  }
}

const styledComponent = withStyles(domainDetailsStyles)(DomainDetails);

export default styledComponent;
